import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { useComments } from '@/hooks/useComments'
import { useSlideCommentCounts } from '@/hooks/useSlideCommentCounts'
import { usePresence } from '@/hooks/usePresence'
import { useAuth } from '@/hooks/useAuth'
import type { SlideInfo } from '@/components/DeckViewer'
import type { Thread } from '@/hooks/useComments'
import type { DeckTheme } from '@/decks/registry'
import './CommentDrawer.css'

const DECK_OWNER_EMAIL = 'ken@globalcomix.com'

const LANG_LABELS: Record<string, string> = { en: 'English', ja: '\u65E5\u672C\u8A9E' }

function langLabel(code: string): string {
  return LANG_LABELS[code] ?? code.toUpperCase()
}

// Inline flag icons — no icon font, no emoji (Windows Chrome draws no flag emoji).
function FlagIcon({ code }: { code: string }) {
  if (code === 'ja') {
    return (
      <svg className="lang-flag" width="20" height="14" viewBox="0 0 20 14" aria-hidden="true">
        <rect width="20" height="14" rx="2" fill="#fff" stroke="#ddd" strokeWidth="0.5" />
        <circle cx="10" cy="7" r="4" fill="#BC002D" />
      </svg>
    )
  }
  return (
    <svg className="lang-flag" width="20" height="14" viewBox="0 0 20 14" aria-hidden="true">
      <rect width="20" height="14" rx="2" fill="#fff" stroke="#ddd" strokeWidth="0.5" />
      <path d="M0 2h20M0 4h20M0 6h20M0 8h20M0 10h20M0 12h20" stroke="#B22234" strokeWidth="1.1" />
      <rect width="9" height="7" rx="1" fill="#3C3B6E" />
    </svg>
  )
}

function LangMenuItem({ code, active, onSelect }: {
  code: string
  active: boolean
  onSelect: (code: string) => void
}) {
  const handleClick = useCallback(() => { onSelect(code) }, [code, onSelect])
  return (
    <button type="button" className={`lang-menu-item ${active ? 'active' : ''}`} onClick={handleClick}>
      <FlagIcon code={code} />
      <span>{langLabel(code)}</span>
      {active && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
      )}
    </button>
  )
}

// Current slide's languages. One language = nothing to switch, so the trigger
// is disabled and says why.
function LangSwitch({ lang, langs, onSelectLang }: {
  lang: string
  langs: string[]
  onSelectLang: (code: string) => void
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const single = langs.length < 2

  const toggle = useCallback(() => { setOpen(o => !o) }, [])
  const select = useCallback((code: string) => {
    onSelectLang(code)
    setOpen(false)
  }, [onSelectLang])

  // Close on an outside click so the menu never outlives its trigger.
  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => { document.removeEventListener('mousedown', onDocClick) }
  }, [open])

  useEffect(() => { if (single) setOpen(false) }, [single])

  return (
    <div className="lang-wrap" ref={wrapRef}>
      <button
        type="button"
        className="lang-trigger"
        onClick={toggle}
        disabled={single}
        aria-haspopup="menu"
        aria-expanded={open}
        title={single ? 'This slide has no translation' : `Language: ${langLabel(lang)}`}
        aria-label={single ? 'This slide has no translation' : `Language: ${langLabel(lang)}`}
      >
        <FlagIcon code={lang} />
        <svg className="lang-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="lang-menu" role="menu">
          {langs.map(code => (
            <LangMenuItem key={code} code={code} active={code === lang} onSelect={select} />
          ))}
        </div>
      )}
    </div>
  )
}

interface CommentDrawerProps {
  deckSlug: string
  slideIndex: number
  slideCount: number
  slides: SlideInfo[]
  hasDocs?: boolean
  onOpenDocs?: () => void
  hasNotes?: boolean
  onOpenNotes?: () => void
  theme: DeckTheme
  onToggleTheme: () => void
  lang: string
  langs: string[]
  onSelectLang: (code: string) => void
  onPrev: () => void
  onNext: () => void
  onJumpTo: (n: number) => void
}

function timeAgo(date: Date | null): string {
  if (!date) return ''
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function ThreadItem({ thread, user, onReply, onDelete, onResolve, onUnresolve }: {
  thread: Thread
  user: { uid: string; email: string | null; displayName: string | null; photoURL: string | null } | null
  onReply: (parentId: string, text: string) => Promise<void>
  onDelete: (commentId: string) => Promise<void>
  onResolve: (commentId: string) => Promise<void>
  onUnresolve: (commentId: string) => Promise<void>
}) {
  const [replyOpen, setReplyOpen] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [expandedResolved, setExpandedResolved] = useState(false)
  const replyInputRef = useRef<HTMLInputElement>(null)
  const c = thread.comment
  const isResolved = c.resolved
  const canResolve = user?.email === DECK_OWNER_EMAIL

  useEffect(() => {
    if (replyOpen && replyInputRef.current) {
      replyInputRef.current.focus()
    }
  }, [replyOpen])

  async function handleReplySubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!replyText.trim()) return
    await onReply(c.id, replyText)
    setReplyText('')
    setReplyOpen(false)
  }

  if (isResolved && !expandedResolved) {
    return (
      <div className="thread thread-resolved" onClick={() => setExpandedResolved(true)}>
        <div className="comment-item">
          <div className="comment-avatar">
            {c.photoURL ? <img src={c.photoURL} alt="" /> : <span>{(c.displayName || '?')[0]}</span>}
          </div>
          <div className="comment-body">
            <div className="comment-meta">
              <span className="comment-name">{c.displayName}</span>
              <span className="thread-resolved-badge">Resolved</span>
            </div>
            <div className="comment-text text-truncate">{c.text}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`thread ${isResolved ? 'thread-resolved-expanded' : ''}`}>
      <div className="comment-item">
        <div className="comment-avatar">
          {c.photoURL ? <img src={c.photoURL} alt="" /> : <span>{(c.displayName || '?')[0]}</span>}
        </div>
        <div className="comment-body">
          <div className="comment-meta">
            <span className="comment-name">{c.displayName}</span>
            <span className="comment-time">{timeAgo(c.createdAt)}</span>
            {isResolved && <span className="thread-resolved-badge">Resolved</span>}
            {user && c.userId === user.uid && (
              <button className="comment-delete" onClick={() => onDelete(c.id)} title="Delete">&times;</button>
            )}
          </div>
          <div className="comment-text">{c.text}</div>
          <div className="thread-actions">
            <button className="thread-action-btn" onClick={() => setReplyOpen(!replyOpen)}>
              Reply{thread.replies.length > 0 ? ` (${thread.replies.length})` : ''}
            </button>
            {canResolve && !isResolved && (
              <button className="thread-action-btn thread-action-resolve" onClick={() => onResolve(c.id)}>
                Resolve
              </button>
            )}
            {canResolve && isResolved && (
              <button className="thread-action-btn" onClick={() => onUnresolve(c.id)}>
                Unresolve
              </button>
            )}
            {isResolved && expandedResolved && (
              <button className="thread-action-btn" onClick={() => setExpandedResolved(false)}>
                Collapse
              </button>
            )}
          </div>
        </div>
      </div>

      {thread.replies.length > 0 && (
        <div className="thread-replies">
          {thread.replies.map(r => (
            <div key={r.id} className="comment-item">
              <div className="comment-avatar">
                {r.photoURL ? <img src={r.photoURL} alt="" /> : <span>{(r.displayName || '?')[0]}</span>}
              </div>
              <div className="comment-body">
                <div className="comment-meta">
                  <span className="comment-name">{r.displayName}</span>
                  <span className="comment-time">{timeAgo(r.createdAt)}</span>
                  {user && r.userId === user.uid && (
                    <button className="comment-delete" onClick={() => onDelete(r.id)} title="Delete">&times;</button>
                  )}
                </div>
                <div className="comment-text">{r.text}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {replyOpen && (
        <div className="thread-replies">
          <form className="reply-input" onSubmit={handleReplySubmit}>
            <input
              ref={replyInputRef}
              type="text"
              placeholder="Write a reply..."
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
            />
            <button type="submit" disabled={!replyText.trim()}>Reply</button>
          </form>
        </div>
      )}
    </div>
  )
}

export function CommentDrawer({ deckSlug, slideIndex, slideCount, slides, hasDocs, onOpenDocs, hasNotes, onOpenNotes, theme, onToggleTheme, lang, langs, onSelectLang, onPrev, onNext, onJumpTo }: CommentDrawerProps) {
  const { user, handleSignOut } = useAuth()
  const { threads, addComment, addReply, deleteComment, resolveThread, unresolveThread } = useComments(deckSlug, slideIndex)
  const { counts: commentCounts, totalUnresolved } = useSlideCommentCounts(deckSlug)
  const { viewers } = usePresence(deckSlug, slideIndex)
  const [expanded, setExpanded] = useState(false)
  const [filmstripOpen, setFilmstripOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [showResolved, setShowResolved] = useState(false)
  const [text, setText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const filmstripRef = useRef<HTMLDivElement>(null)
  const prevThreadCountRef = useRef(0)

  const unresolvedThreads = useMemo(() => threads.filter(t => !t.comment.resolved), [threads])
  const resolvedThreads = useMemo(() => threads.filter(t => t.comment.resolved), [threads])
  const otherViewers = useMemo(() => viewers.filter(v => v.userId !== user?.uid), [viewers, user])

  // Auto-scroll filmstrip to keep current slide visible
  useEffect(() => {
    if (filmstripOpen && filmstripRef.current) {
      const activeThumb = filmstripRef.current.querySelector('.filmstrip-thumb.active')
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
      }
    }
  }, [slideIndex, filmstripOpen])

  useEffect(() => {
    const currentCount = threads.reduce((n, t) => n + 1 + t.replies.length, 0)
    if (expanded && listRef.current && currentCount > prevThreadCountRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
    prevThreadCountRef.current = currentCount
  }, [threads, expanded])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!text.trim()) return
    await addComment(text)
    setText('')
    inputRef.current?.focus()
  }

  function handleThumbClick(idx: number) {
    onJumpTo(idx)
    setFilmstripOpen(false)
  }

  return (
    <div className={`comment-drawer ${expanded ? 'expanded' : ''}`}>
      {filmstripOpen && (
        <div className="filmstrip" ref={filmstripRef}>
          {slides.map((s) => (
            <button
              key={s.index}
              className={`filmstrip-thumb ${s.index === slideIndex ? 'active' : ''}`}
              onClick={() => handleThumbClick(s.index)}
              title={s.title}
            >
              <span className="filmstrip-num">{s.index + 1}</span>
              <span className="filmstrip-label">{s.label || `Slide ${s.index + 1}`}</span>
              {commentCounts[s.index] > 0 && (
                <span className="filmstrip-comment-dot" title={`${commentCounts[s.index]} comments`}>
                  {commentCounts[s.index]}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="comment-drawer-bar">
        <div className="comment-drawer-nav">
          <button
            className="drawer-nav-btn"
            onClick={onPrev}
            disabled={slideIndex <= 0}
            title="Previous slide"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
          </button>
          <button
            className="drawer-slide-num"
            onClick={() => setFilmstripOpen(!filmstripOpen)}
            title="Show slide filmstrip"
          >
            {slideIndex + 1} / {slideCount}
          </button>
          <button
            className="drawer-nav-btn"
            onClick={onNext}
            disabled={slideIndex >= slideCount - 1}
            title="Next slide"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
          </button>
        </div>

        <span className="drawer-divider" />

        <button className="comment-drawer-toggle" onClick={() => setExpanded(!expanded)} type="button">
          <span className="comment-drawer-label">
            Comments {unresolvedThreads.length > 0 && `(${unresolvedThreads.length})`}
          </span>
          <span className="comment-drawer-badge">{totalUnresolved} total</span>
          <span className="comment-drawer-chevron">{expanded ? '\u25BC' : '\u25B2'}</span>
        </button>

        <span className="drawer-divider" />

        {otherViewers.length > 0 && (
          <div className="bar-presence">
            {otherViewers.slice(0, 4).map(v => (
              <div key={v.userId} className="bar-avatar" title={`${v.displayName} — Slide ${v.currentSlide + 1}`}>
                {v.photoURL ? <img src={v.photoURL} alt={v.displayName} /> : <span>{(v.displayName || '?')[0]}</span>}
              </div>
            ))}
          </div>
        )}

        <LangSwitch lang={lang} langs={langs} onSelectLang={onSelectLang} />

        <button
          className="drawer-theme-toggle"
          onClick={onToggleTheme}
          type="button"
          title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        >
          {theme === 'dark' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
          )}
        </button>

        <div className="bar-user-wrap">
          <button className="bar-user-avatar" onClick={() => setUserMenuOpen(!userMenuOpen)} title={user?.displayName || 'Account'}>
            {user?.photoURL
              ? <img src={user.photoURL} alt="" />
              : <span>{(user?.displayName || user?.email || '?')[0]}</span>
            }
          </button>
          {userMenuOpen && (
            <div className="bar-user-menu">
              <div className="bar-user-info">
                <div className="bar-user-info-avatar">
                  {user?.photoURL
                    ? <img src={user.photoURL} alt="" />
                    : <span>{(user?.displayName || user?.email || '?')[0]}</span>
                  }
                </div>
                <div className="bar-user-info-text">
                  <div className="bar-user-name">{user?.displayName}</div>
                  <div className="bar-user-email">{user?.email}</div>
                </div>
              </div>
              {hasDocs && (
                <button className="bar-user-action" onClick={() => { setUserMenuOpen(false); onOpenDocs?.() }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>
                  <span>View Docs</span>
                </button>
              )}
              {hasNotes && (
                <button className="bar-user-action" onClick={() => { setUserMenuOpen(false); onOpenNotes?.() }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6h4"/><path d="M2 10h4"/><path d="M2 14h4"/><path d="M2 18h4"/><rect width="16" height="20" x="4" y="2" rx="2"/><path d="M9.5 8h5"/><path d="M9.5 12H16"/><path d="M9.5 16H14"/></svg>
                  <span>Speaker Notes</span>
                </button>
              )}
              <button className="bar-user-signout" onClick={handleSignOut}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <div className="comment-drawer-content">
          <div className="comment-list" ref={listRef}>
            {unresolvedThreads.length === 0 && resolvedThreads.length === 0 && (
              <div className="comment-empty">No comments on this slide yet</div>
            )}
            {unresolvedThreads.length === 0 && resolvedThreads.length > 0 && !showResolved && (
              <div className="comment-empty">No open comments on this slide</div>
            )}

            {unresolvedThreads.map(t => (
              <ThreadItem
                key={t.comment.id}
                thread={t}
                user={user}
                onReply={addReply}
                onDelete={deleteComment}
                onResolve={resolveThread}
                onUnresolve={unresolveThread}
              />
            ))}

            {resolvedThreads.length > 0 && (
              <button
                className="resolved-toggle"
                onClick={() => setShowResolved(!showResolved)}
              >
                {showResolved ? 'Hide' : 'Show'} resolved ({resolvedThreads.length})
              </button>
            )}

            {showResolved && resolvedThreads.map(t => (
              <ThreadItem
                key={t.comment.id}
                thread={t}
                user={user}
                onReply={addReply}
                onDelete={deleteComment}
                onResolve={resolveThread}
                onUnresolve={unresolveThread}
              />
            ))}
          </div>
          <form className="comment-input" onSubmit={handleSubmit}>
            <input ref={inputRef} type="text" placeholder="Add a comment..." value={text} onChange={e => setText(e.target.value)} />
            <button type="submit" disabled={!text.trim()}>Send</button>
          </form>
        </div>
      )}
    </div>
  )
}
